import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { sb, SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useComms } from "../lib/commsReady";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";

// Two-way SMS chat. Reusable for both the Lead Center (scope="lead") and Clients
// (scope="client"). Left = conversation list, right = the selected thread + composer.
// Reads the org's `messages` directly (RLS-scoped); sends via the send-sms edge function.
const authHeaders = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
const last10 = (p) => (p || "").replace(/\D/g, "").slice(-10);
const fmtPhone = (p) => { const d = (p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || ""); };
const fmtTime = (iso) => { const d = new Date(iso); const now = new Date(); const sameDay = d.toDateString() === now.toDateString(); return sameDay ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" }); };

export function Conversations({ scope, rows = [], showToast, openId }) {
  const { textingReady, guideTexting } = useComms();
  const idKey = scope === "client" ? "client_id" : "lead_id";
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);   // selected lead/client id
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);
  const [pickSearch, setPickSearch] = useState("");   // recipient search — its OWN state, separate from the conversation-list search
  const threadRef = useRef(null);
  const isMobile = useIsMobile();

  const nameFor = useCallback((r) => scope === "client" ? (r.name || fmtPhone(r.phone) || "Client") : ([r.first_name, r.last_name].filter(Boolean).join(" ") || fmtPhone(r.phone) || "Lead"), [scope]);
  const rowById = useMemo(() => { const m = {}; for (const r of rows) m[r.id] = r; return m; }, [rows]);

  const load = useCallback(async () => {
    try {
      const url = `${SUPABASE_URL}/rest/v1/messages?order=created_at.asc&${idKey}=not.is.null&select=id,${idKey},phone,direction,body,status,read_at,created_at`;
      const data = await fetch(url, { headers: authHeaders() }).then(r => r.json());
      if (Array.isArray(data)) setMessages(data);
    } catch { /* keep prior */ }
  }, [idKey]);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  // Open a specific thread on demand (e.g. the "Text" button on a client/lead row).
  useEffect(() => { if (openId != null) { setActiveId(openId); setPicking(false); } }, [openId]);

  // Build conversations: group messages by lead/client id.
  const convos = useMemo(() => {
    const byId = new Map();
    for (const m of messages) {
      const id = m[idKey];
      if (!byId.has(id)) byId.set(id, { id, msgs: [], lastAt: m.created_at, unread: 0, phone: m.phone });
      const c = byId.get(id);
      c.msgs.push(m); c.lastAt = m.created_at; c.phone = m.phone || c.phone;
      if (m.direction === "in" && !m.read_at) c.unread++;
    }
    return [...byId.values()].sort((a, b) => new Date(b.lastAt) - new Date(a.lastAt));
  }, [messages, idKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return convos;
    return convos.filter(c => { const r = rowById[c.id]; const n = r ? nameFor(r).toLowerCase() : ""; return n.includes(q) || last10(c.phone).includes(q.replace(/\D/g, "")); });
  }, [convos, search, rowById, nameFor]);

  const active = activeId != null ? (convos.find(c => c.id === activeId) || { id: activeId, msgs: [], phone: rowById[activeId]?.phone }) : null;
  const activeRow = activeId != null ? rowById[activeId] : null;

  useEffect(() => {
    if (!threadRef.current) return;
    if (isMobile) {
      // Phones: the thread flows in the document (no nested scroller) — jump the page to the
      // newest message + composer, like opening a chat.
      try { window.scrollTo({ top: document.documentElement.scrollHeight }); } catch { /* */ }
    } else {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [active?.msgs?.length, activeId, isMobile]);

  // Phones are a master-detail: list OR (thread | picker). The iOS back gesture returns to the list.
  const mobileDetail = isMobile && (activeId != null || picking);
  const backToList = () => { setActiveId(null); setPicking(false); setPickSearch(""); };
  useBackClose(mobileDetail, backToList);
  const showList = !isMobile || !mobileDetail;
  const showThread = !isMobile || mobileDetail;

  // Mark inbound read when a thread is opened.
  const openConvo = async (id) => {
    setActiveId(id); setPicking(false);
    const unreadIds = (convos.find(c => c.id === id)?.msgs || []).filter(m => m.direction === "in" && !m.read_at).map(m => m.id);
    if (unreadIds.length) {
      try {
        await fetch(`${SUPABASE_URL}/rest/v1/messages?id=in.(${unreadIds.join(",")})`, { method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json", Prefer: "return=minimal" }, body: JSON.stringify({ read_at: new Date().toISOString() }) });
        setMessages(ms => ms.map(m => unreadIds.includes(m.id) ? { ...m, read_at: new Date().toISOString() } : m));
      } catch { /* */ }
    }
  };

  async function send() {
    const text = draft.trim();
    const to = active?.phone || activeRow?.phone;
    if (!text || !to || sending) return;
    setSending(true);
    try {
      const r = await sb.fn("send-sms", { to, body: text, [idKey]: activeId });
      if (r?.error) { showToast && showToast(r.error, "error"); }
      else { setDraft(""); setMessages(ms => [...ms, r.message]); }
    } catch { showToast && showToast("Couldn't send — try again", "error"); }
    setSending(false);
  }

  async function callContact() {
    const to = active?.phone || activeRow?.phone;
    if (!to) return;
    // Prefer the in-browser softphone (the call rings + is answered right here). The
    // ring-my-cell path below stays as a fallback when the softphone isn't ready.
    if (typeof window !== "undefined" && window.scoopDial) {
      window.scoopDial(to, activeRow ? nameFor(activeRow) : fmtPhone(to));
      showToast && showToast("📞 Calling…");
      return;
    }
    let op = (localStorage.getItem("scoop_call_from") || "").trim();
    if (!op) {
      const entered = window.prompt(`Your phone number — we'll ring it first, then connect you to ${activeRow ? nameFor(activeRow) : fmtPhone(to)}:`);
      op = (entered || "").replace(/[^\d+]/g, "");
      if (!op) return;
      localStorage.setItem("scoop_call_from", op);
    }
    try {
      const r = await sb.fn("twilio-call", { to, operator: op, [idKey]: activeId });
      if (r?.error) showToast && showToast(r.error, "error");
      else showToast && showToast(`📞 Calling — your phone (${fmtPhone(op)}) rings first, then we connect you.`);
    } catch { showToast && showToast("Couldn't start the call — try again", "error"); }
  }

  const withPhone = rows.filter(r => last10(r.phone).length === 10);

  return (
    <>
      {!textingReady && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: "#FEF6E7", border: "1px solid #F6D88A", borderRadius: "var(--r, 12px)", padding: "10px 14px", marginBottom: 12 }}>
          <span style={{ fontSize: 16 }} aria-hidden="true">📱</span>
          <span style={{ fontSize: 13, color: "#8A5A06", flex: "1 1 200px", minWidth: 0 }}>Texting isn't set up yet — finish setup to send and receive messages.</span>
          <button className="btn btn-gold btn-sm" onClick={guideTexting} style={isMobile ? { width: "100%", minHeight: 40 } : undefined}>Set up texting →</button>
        </div>
      )}
      <div className={`conv${isMobile ? " conv-m" : ""}`}>
      <style>{CONV_CSS}</style>

      {/* ── conversation list ── */}
      {showList && <div className="conv-list">
        <div className="conv-lhead">
          <input className="conv-search" placeholder="Search messages…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="conv-new" title="New message" onClick={() => { setPicking(true); setActiveId(null); setPickSearch(""); }}>＋</button>
        </div>
        <div className="conv-rows">
          {filtered.length === 0 && <div className="conv-empty">No conversations yet. Tap ＋ to text a {scope}.</div>}
          {filtered.map(c => {
            const r = rowById[c.id]; const lastMsg = c.msgs[c.msgs.length - 1];
            return (
              <button key={c.id} className={`conv-row${c.id === activeId ? " on" : ""}`} onClick={() => openConvo(c.id)}>
                <span className="conv-av">{(r ? nameFor(r) : "?").slice(0, 1).toUpperCase()}</span>
                <span className="conv-rmid">
                  <span className="conv-rname">{r ? nameFor(r) : fmtPhone(c.phone)}</span>
                  <span className="conv-rprev">{lastMsg ? (lastMsg.direction === "out" ? "You: " : "") + lastMsg.body : ""}</span>
                </span>
                <span className="conv-rmeta">
                  <span className="conv-rtime">{lastMsg ? fmtTime(lastMsg.created_at) : ""}</span>
                  {c.unread > 0 && <span className="conv-unread">{c.unread}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>}

      {/* ── thread / picker ── */}
      {showThread && <div className="conv-thread">
        {picking ? (
          <div className="conv-pick">
            <div className="conv-thead">
              {isMobile && <button type="button" className="conv-back" onClick={backToList} aria-label="Back to conversations">‹ Back</button>}
              <span className="conv-tname">New message</span>
              {!isMobile && <button className="conv-call" onClick={() => { setPicking(false); setPickSearch(""); }}>Cancel</button>}
            </div>
            <div className="conv-pickbar">
              <span className="conv-pickto">To</span>
              <input className="conv-pickinput" placeholder={`Search ${scope}s by name or number…`} value={pickSearch} onChange={e => setPickSearch(e.target.value)} autoFocus />
            </div>
            <div className="conv-picklist">
              {(() => {
                const q = pickSearch.trim().toLowerCase();
                if (withPhone.length === 0) return <div className="conv-empty">No {scope}s with a phone number yet.</div>;
                const matches = withPhone.filter(r => !q || nameFor(r).toLowerCase().includes(q) || last10(r.phone).includes(q.replace(/\D/g, "")));
                if (matches.length === 0) return <div className="conv-empty">No {scope}s match “{pickSearch}”.</div>;
                return matches.slice(0, 60).map(r => (
                  <button key={r.id} className="conv-pickrow" onClick={() => { setPickSearch(""); setPicking(false); openConvo(r.id); }}>
                    <span className="conv-av sm">{nameFor(r).slice(0, 1).toUpperCase()}</span>
                    <span className="conv-rname">{nameFor(r)}</span><span className="conv-rprev">{fmtPhone(r.phone)}</span>
                  </button>
                ));
              })()}
            </div>
          </div>
        ) : !active ? (
          <div className="conv-blank">Select a conversation, or tap ＋ to start one.</div>
        ) : (
          <>
            <div className="conv-thead">
              {isMobile && <button type="button" className="conv-back" onClick={backToList} aria-label="Back to conversations">‹ Back</button>}
              <span className="conv-tid">
                <span className="conv-tname">{activeRow ? nameFor(activeRow) : fmtPhone(active.phone)}</span>
                <span className="conv-tphone">{fmtPhone(active.phone)}</span>
              </span>
              <button className="conv-call" onClick={callContact} title="Call — rings your phone first, then connects you">📞 Call</button>
            </div>
            <div className="conv-msgs" ref={threadRef}>
              {active.msgs.length === 0 && <div className="conv-empty">No messages yet — say hi 👋</div>}
              {active.msgs.map(m => (
                <div key={m.id} className={`conv-bub ${m.direction === "out" ? "out" : "in"}`}>
                  <div className="conv-bbody">{m.body}</div>
                  <div className="conv-btime">{fmtTime(m.created_at)}{m.direction === "out" && m.status === "failed" ? " · failed" : ""}</div>
                </div>
              ))}
            </div>
            <div className="conv-compose">
              <textarea value={draft} onChange={e => setDraft(e.target.value)} placeholder="Type a message…" rows={1}
                onKeyDown={e => { if (isMobile) return; /* phone keyboards have no Shift+Enter — return = newline, Send sends */ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <button className="btn btn-gold btn-sm" disabled={sending || !draft.trim()} onClick={send}>{sending ? "…" : "Send"}</button>
            </div>
          </>
        )}
      </div>}
    </div>
    </>
  );
}

const CONV_CSS = `
.conv{display:flex;height:540px;max-height:70vh;border:1px solid var(--border);border-radius:var(--r,12px);overflow:hidden;background:var(--surface)}
.conv-list{width:300px;flex-shrink:0;border-right:1px solid var(--border);display:flex;flex-direction:column;background:var(--bg)}
.conv-lhead{display:flex;gap:8px;padding:10px;border-bottom:1px solid var(--border)}
.conv-search{flex:1;min-width:0;padding:8px 11px;border:1px solid var(--border);border-radius:999px;font-size:13px;background:var(--surface);color:var(--text)}
.conv-search:focus{outline:none;border-color:var(--accent)}
.conv-new{flex-shrink:0;width:34px;border:1px solid var(--border);border-radius:50%;background:var(--surface);color:var(--accent);font-size:18px;font-weight:700;cursor:pointer}
.conv-rows{flex:1;overflow-y:auto}
.conv-row{display:flex;align-items:center;gap:10px;width:100%;padding:10px 12px;background:none;border:none;border-bottom:1px solid var(--border);cursor:pointer;text-align:left}
.conv-row:hover{background:var(--surface)}
.conv-row.on{background:var(--accent-weak)}
.conv-av{flex-shrink:0;width:36px;height:36px;border-radius:50%;background:var(--accent-weak);color:var(--accent);font-weight:700;font-size:14px;display:flex;align-items:center;justify-content:center}
.conv-av.sm{width:30px;height:30px;font-size:12px}
.conv-rmid{flex:1;min-width:0;display:flex;flex-direction:column}
.conv-rname{font-size:13.5px;font-weight:600;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.conv-rprev{font-size:12px;color:var(--text2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.conv-rmeta{display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0}
.conv-rtime{font-size:11px;color:var(--text3)}
.conv-unread{background:var(--accent);color:#fff;font-size:10.5px;font-weight:700;min-width:18px;height:18px;border-radius:999px;display:flex;align-items:center;justify-content:center;padding:0 5px}
.conv-thread{flex:1;display:flex;flex-direction:column;min-width:0}
.conv-thead{padding:13px 16px;border-bottom:1px solid var(--border);display:flex;align-items:baseline;gap:10px}
.conv-tid{display:contents}
.conv-tname{font-size:14.5px;font-weight:600;color:var(--text)}
@media (max-width:768px){.conv-m .conv-tname{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}}
.conv-tphone{font-size:12.5px;color:var(--text2)}
.conv-call{margin-left:auto;flex-shrink:0;border:1px solid var(--border);background:var(--surface);color:var(--accent);font-size:12.5px;font-weight:600;padding:5px 13px;border-radius:999px;cursor:pointer}
.conv-back{display:none}
.conv-call:hover{background:var(--accent-weak)}
.conv-msgs{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:8px;background:var(--bg)}
.conv-bub{max-width:74%;padding:8px 12px;border-radius:16px;font-size:13.5px;line-height:1.45}
.conv-bub.in{align-self:flex-start;background:var(--surface);border:1px solid var(--border);color:var(--text);border-bottom-left-radius:5px}
.conv-bub.out{align-self:flex-end;background:var(--accent);color:#fff;border-bottom-right-radius:5px}
.conv-bbody{white-space:pre-wrap;word-break:break-word}
.conv-btime{font-size:10.5px;margin-top:3px;opacity:.7}
.conv-bub.out .conv-btime{text-align:right}
.conv-compose{display:flex;gap:8px;align-items:flex-end;padding:11px 12px;border-top:1px solid var(--border)}
.conv-compose textarea{flex:1;resize:none;max-height:120px;padding:9px 12px;border:1px solid var(--border);border-radius:16px;font-size:13.5px;font-family:inherit;line-height:1.4;background:var(--surface);color:var(--text)}
.conv-compose textarea:focus{outline:none;border-color:var(--accent)}
.conv-blank,.conv-empty,.conv-pick .conv-empty{padding:24px;text-align:center;color:var(--text2);font-size:13px;margin:auto}
.conv-empty{margin:0;padding:18px}
.conv-blank{display:flex;align-items:center;justify-content:center;height:100%}
.conv-pick{display:flex;flex-direction:column;height:100%;min-height:0}
.conv-pickbar{display:flex;align-items:center;gap:9px;padding:11px 14px;border-bottom:1px solid var(--border)}
.conv-pickto{font-size:12.5px;font-weight:600;color:var(--text2);flex-shrink:0}
.conv-pickinput{flex:1;min-width:0;padding:9px 12px;border:1px solid var(--border);border-radius:var(--r-sm,8px);font-size:13.5px;background:var(--surface);color:var(--text)}
.conv-pickinput:focus{outline:none;border-color:var(--accent)}
.conv-picklist{flex:1;min-height:0;overflow-y:auto}
.conv-pickrow{display:flex;align-items:center;gap:10px;width:100%;padding:9px 14px;background:none;border:none;border-bottom:1px solid var(--border);cursor:pointer;text-align:left}
.conv-pickrow:hover{background:var(--bg)}
.conv-pickrow .conv-rprev{margin-left:auto;flex:none}
/* ── Phones (≤768px, paired with useIsMobile): master-detail. The list OR the thread renders,
   full width, and everything flows into the DOCUMENT scroll — no nested scrollers (iOS
   scroll-chains between them and 70vh is measured against the collapsed-toolbar viewport).
   Only the composer is sticky (bottom) and the thread header (under the 60px topbar). ── */
@media (max-width:768px){
  .conv.conv-m{flex-direction:column;height:auto;max-height:none;overflow:visible}
  .conv-m .conv-list{width:100%;flex-shrink:1;border-right:none;border-radius:var(--r,12px);overflow:hidden}
  .conv-m .conv-lhead{padding:10px 12px}
  .conv-m .conv-search{padding:10px 14px;min-height:40px}
  .conv-m .conv-new{width:40px;height:40px;min-width:40px}
  .conv-m .conv-rows{flex:none;overflow:visible;min-height:200px}
  .conv-m .conv-row{padding:12px;min-height:60px}
  .conv-m .conv-thread{min-width:0}
  .conv-m .conv-thead{position:sticky;top:var(--conv-sticky-top,60px);z-index:5;background:var(--surface);align-items:center;padding:10px 12px;gap:8px}
  .conv-m .conv-tid{display:flex;flex-direction:column;min-width:0;flex:1}
  .conv-m .conv-tphone{font-size:12px}
  .conv-m .conv-back{display:inline-flex;align-items:center;flex-shrink:0;min-height:40px;padding:0 10px 0 4px;margin-left:-4px;border:none;background:transparent;color:var(--accent);font-size:15px;font-weight:600;cursor:pointer}
  .conv-m .conv-call{padding:9px 14px;min-height:40px}
  .conv-m .conv-msgs{flex:none;overflow:visible;min-height:220px;padding:12px}
  .conv-m .conv-bub{max-width:85%}
  .conv-m .conv-compose{position:sticky;bottom:0;z-index:5;background:var(--surface);padding:10px 12px calc(10px + env(safe-area-inset-bottom,0px));box-shadow:0 -6px 16px -10px rgba(0,0,0,.18)}
  .conv-m .conv-compose textarea{min-height:44px;min-width:0}
  .conv-m .conv-compose .btn{min-height:44px;flex-shrink:0}
  .conv-m .conv-pick{height:auto}
  .conv-m .conv-pickbar{position:sticky;top:calc(var(--conv-sticky-top,60px) + 61px);z-index:4;background:var(--surface)}
  .conv-m .conv-pickinput{min-height:44px}
  .conv-m .conv-picklist{flex:none;overflow:visible;min-height:200px}
  .conv-m .conv-pickrow{min-height:52px;padding:10px 14px}
  .conv-m .conv-blank{height:auto;padding:40px 24px}
}
`;
